import { Injectable, inject } from '@angular/core';
import { IStoredUser, TokenStorageService } from '@dlap-ui/core';
import { TicketMessageRecord } from './tickets-api.models';

export function isOwnTicketMessage(
  message: { senderUserId?: string | null; username?: string | null; senderUsername?: string | null },
  user: IStoredUser | null,
): boolean {
  // The live API can return an email alias and an incorrect senderType.
  if (message.senderUserId && user?.id) return message.senderUserId === user.id;
  const sender = (message.senderUsername ?? message.username ?? '').trim().toLowerCase();
  return !!sender && [user?.username, user?.email].some(
    (identity) => identity?.trim().toLowerCase() === sender,
  );
}

@Injectable({ providedIn: 'root' })
export class TicketReadStateService {
  private readonly tokens = inject(TokenStorageService);
  private readonly readIds = new Map<string, Set<string>>();

  private key(ticketId: string): string {
    return `ticket-read:${this.tokens.getUser()?.id ?? 'anonymous'}:${ticketId}`;
  }

  private ids(ticketId: string): Set<string> {
    const key = this.key(ticketId);
    let ids = this.readIds.get(key);
    if (!ids) {
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
        ids = new Set(Array.isArray(saved) ? saved.filter((id) => typeof id === 'string') : []);
      } catch {
        ids = new Set();
      }
      this.readIds.set(key, ids);
    }
    return ids;
  }

  unreadCount(ticketId: string, messages: TicketMessageRecord[]): number {
    const user = this.tokens.getUser();
    const readIds = this.ids(ticketId);
    return messages.filter((message) =>
      !isOwnTicketMessage(message, user) && message.isRead !== true &&
      !(message.id && readIds.has(message.id)),
    ).length;
  }

  markRead(ticketId: string, messages: TicketMessageRecord[]): void {
    const ids = this.ids(ticketId);
    messages.forEach((message) => { if (message.id) ids.add(message.id); });
    try {
      localStorage.setItem(this.key(ticketId), JSON.stringify([...ids]));
    } catch {
      // Keep the current session working if browser storage is unavailable.
    }
  }
}
